import type { LinkMouseEvent } from "@/hooks/analytics/types";

import { useSegmentAnalytics } from "./useSegmentAnalytics";

type ClickEvent = {
  country: string;
  warningShown?: boolean;
  program?: string;
  link_url?: string;
};

type MAMProgramSelectionEvent = {
  linkText: string | string[];
  elementName: string;
};

export const useTriagePageAnalytics = () => {
  const {
    trackButtonClickEvent,
    trackMenuClickEvent,
    trackExternalLinkClickEvent,
    trackOptionClickEvent,
  } = useSegmentAnalytics();

  const trackCOOContinueClickEvent = ({ country }: ClickEvent) =>
    trackButtonClickEvent({
      label: "Continue",
      location: "COO selection",
      extraProperties: {
        country_selected: country,
        event_type: "Click",
      },
    });

  const trackProgramSelectionBackClickEvent = () =>
    trackButtonClickEvent({
      label: "Back",
      location: "Program selection",
      extraProperties: {
        event_type: "Click",
      },
    });

  const trackBannedCOOBackClickEvent = ({ country }: ClickEvent) =>
    trackButtonClickEvent({
      label: "Back",
      location: "Banned COO form",
      extraProperties: {
        country_selected: country,
        program_selected: "Banned COO",
        event_type: "Click",
      },
    });

  const trackBannedCOOContinueClickEvent = ({
    country,
    warningShown,
  }: ClickEvent) =>
    trackButtonClickEvent({
      label: "Continue",
      location: "Banned COO form",
      extraProperties: {
        country_selected: country,
        program_selected: "Banned COO",
        warning_shown: warningShown,
        event_type: "Click",
      },
    });

  const trackBannedCOOCompleteExternalLinkClickEvent = ({
    event,
    country,
    link_url,
  }: ClickEvent & {
    event: LinkMouseEvent;
  }) =>
    trackExternalLinkClickEvent({
      event,
      label: "Exit application",
      location: "Application complete",
      extraProperties: {
        country_selected: country,
        program_selected: "Banned COO",
        event_type: "External link clicked",
        link_url,
      },
    });

  const trackProgramCompleteExternalLinkClickEvent = ({
    event,
    country,
    program,
    link_url,
  }: ClickEvent & {
    event: LinkMouseEvent;
  }) =>
    trackExternalLinkClickEvent({
      event,
      label: "Continue to application",
      location: "Application complete",
      extraProperties: {
        country_selected: country,
        program_selected: program,
        event_type: "External link clicked",
        link_url,
      },
    });

  const trackProgramSelectionClickEvent = ({ program, country }: ClickEvent) =>
    trackOptionClickEvent({
      label: program,
      location: "Program selection",
      extraProperties: {
        country_selected: country,
        program_selected: program,
        event_type: "Option clicked",
      },
    });

  const trackTooltipClickEvent = ({ country, program }: ClickEvent) =>
    trackButtonClickEvent({
      label: "Tooltip",
      location: program,
      extraProperties: {
        country_selected: country,
        program_selected: program,
        event_type: "Click",
      },
    });

  const trackConfirmationContinueLinkEvent = ({
    event,
    country,
    program,
    link_url,
  }: ClickEvent & { event: LinkMouseEvent }) => {
    trackExternalLinkClickEvent({
      event,
      label: "Continue to application",
      location: program,
      extraProperties: {
        country_selected: country,
        program_selected: program,
        event_type: "External link clicked",
        link_url,
      },
    });
  };

  const trackConfirmationContinueClickEvent = ({
    program,
    country,
  }: ClickEvent) => {
    trackButtonClickEvent({
      label: "Continue to application",
      location: program,
      extraProperties: {
        country_selected: country,
        program_selected: program,
        event_type: "Click",
      },
    });
  };

  const trackConfirmationStartOverClickEvent = ({
    country,
    program,
  }: ClickEvent) => {
    trackButtonClickEvent({
      label: "Start over",
      location: program,
      extraProperties: {
        country_selected: country,
        program_selected: program,
        event_type: "Click",
      },
    });
  };

  const trackProgramSelectionContinueClickEvent = ({
    country,
    program,
    warningShown,
  }: ClickEvent) =>
    trackButtonClickEvent({
      label: "Continue",
      location: "Program selection",
      extraProperties: {
        country_selected: country,
        program_selected: program,
        warning_shown: warningShown,
        event_type: "Click",
      },
    });

  const trackAddAnotherProgramProfileMenuEvent = (event: LinkMouseEvent) =>
    trackMenuClickEvent({
      event,
      label: "Add another program",
      location: "Profile menu",
      extraProperties: {
        event_type: "Menu clicked",
      },
    });

  const trackHybridFormBackClickEvent = ({ country }: ClickEvent) =>
    trackButtonClickEvent({
      label: "Back",
      location: "Hybrid form",
      extraProperties: {
        event_type: "Click",
        country_selected: country,
        program_selected: "Hybrid program",
      },
    });

  const trackHybridFormContinueClickEvent = ({
    country,
    warningShown,
  }: ClickEvent) =>
    trackButtonClickEvent({
      label: "Continue",
      location: "Hybrid form",
      extraProperties: {
        event_type: "Click",
        country_selected: country,
        program_selected: "Hybrid program",
        warning_shown: warningShown,
      },
    });

  const trackMAMFormBackClickEvent = ({ country }: ClickEvent) =>
    trackButtonClickEvent({
      label: "Back",
      location: "MAM qualification form",
      extraProperties: {
        event_type: "Click",
        country_selected: country,
        program_selected: "MAM program",
      },
    });

  const trackMAMFormContinueClickEvent = ({
    country,
    warningShown,
  }: ClickEvent) =>
    trackButtonClickEvent({
      label: "Continue",
      location: "MAM qualification form",
      extraProperties: {
        event_type: "Click",
        country_selected: country,
        program_selected: "MAM program",
        warning_shown: warningShown,
      },
    });

  const trackMAMProgramSelectionContinueClickEvent = ({
    linkText,
    elementName,
  }: MAMProgramSelectionEvent) =>
    trackButtonClickEvent({
      label: "Continue",
      location: "MAM Program selection page",
      extraProperties: {
        link_text: linkText,
        event_type: "Click",
        element_name: elementName,
      },
    });

  const trackMAMProgramSelectionOptionClickEvent = ({
    elementName,
    linkText,
  }: MAMProgramSelectionEvent) =>
    trackOptionClickEvent({
      label: "Option clicked",
      location: "MAM Program selection page - checkbox",
      extraProperties: {
        link_text: linkText as string,
        element_name: elementName,
        event_type: "Option clicked (checkbox)",
      },
    });

  const trackCustomFormBackClickEvent = ({ country }: ClickEvent) =>
    trackButtonClickEvent({
      label: "Back",
      location: "Custom form",
      extraProperties: {
        event_type: "Click",
        country_selected: country,
        program_selected: "Custom program",
      },
    });

  const trackCustomFormContinueClickEvent = ({
    country,
    warningShown,
  }: ClickEvent) =>
    trackButtonClickEvent({
      label: "Continue",
      location: "Custom form",
      extraProperties: {
        event_type: "Click",
        country_selected: country,
        program_selected: "Custom program",
        warning_shown: warningShown,
      },
    });

  return {
    trackCOOContinueClickEvent,
    trackProgramSelectionBackClickEvent,
    trackAddAnotherProgramProfileMenuEvent,
    trackBannedCOOBackClickEvent,
    trackBannedCOOContinueClickEvent,
    trackBannedCOOCompleteExternalLinkClickEvent,
    trackProgramSelectionClickEvent,
    trackProgramSelectionContinueClickEvent,
    trackTooltipClickEvent,
    trackConfirmationContinueClickEvent,
    trackConfirmationContinueLinkEvent,
    trackConfirmationStartOverClickEvent,
    trackHybridFormBackClickEvent,
    trackHybridFormContinueClickEvent,
    trackMAMFormBackClickEvent,
    trackMAMFormContinueClickEvent,
    trackMAMProgramSelectionContinueClickEvent,
    trackMAMProgramSelectionOptionClickEvent,
    trackCustomFormBackClickEvent,
    trackCustomFormContinueClickEvent,
    trackProgramCompleteExternalLinkClickEvent,
  };
};
